<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Result Data Loading...
  </div>

  <div v-else class="animated fadeIn">
    <basic-heading class="mb-3">
      <span class="float-left mr-2">Results: {{ submission.publicNumber() }}</span>

      <badge-batch :badge_collection="submission_badges" />

      <template #right-content>
        <header-button-group :button_config="button_config">
          <template #post_buttons>
            <b-dropdown variant="success" right class="ml-1" :disabled="isDownloading">
              <template #button-content>
                <i v-if="isDownloading" class="fa fa-spinner fa-spin" />
                <i v-else class="fal fa-download" />
              </template>

              <b-dropdown-item @click="downloadFile('csv')">
                <i class="far fa-file-download text-dark mr-0" /> CSV
              </b-dropdown-item>
              <b-dropdown-item @click="downloadFile('modus')">
                <i class="far fa-file-download text-dark mr-0" /> MODUS
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </header-button-group>
      </template>
    </basic-heading>

    <permission-alert-gate :permissions="['read submission']">
      <template #gated-content>
        <status-progress
          :current_status="current_status"
          :log_collection="submission_status_logs"
          status_group="submission"
          class="mb-3"
        />

        <context-pills :items="submission_items" class="mb-3" />

        <ResultCard v-if="result && submission" :result="result" :submission="submission" />

        <div>
          <h3 class="mb-3">
            Samples
          </h3>
          <div v-if="samples" class="row">
            <div v-for="sample in samples.models" :key="sample.id" class="col-6">
              <b-card no-body>
                <div>
                  <h5>Sample #{{ sample.name }}</h5>

                  <ResultSampleAnalyteTable
                    :sample="sample"
                  />
                </div>
              </b-card>
            </div>
          </div>
        </div>
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from 'vue'
import VueNotifications from 'vue-notifications'
import BasicHeading from '@/components/shared/BasicHeading'
import { getCollection, getModel } from '@/shared/fetch-utils'
import ToastNotifications from '@/components/shared/mixins/ToastNotifications'
import { notificationOptions } from '@/shared/toast-notification-options'
import PermissionAlertGate from '@/components/permission/PermissionAlertGate'
import ResultCard from '@/components/modus/result/ResultCard'
import ContextPills from '@/components/shared/ContextPills'
import SampleCollection from '@/collections/result/SampleCollection'
import ResultSampleAnalyteTable from '@/components/modus/result/ResultSampleAnalyteTable'
import StatusProgress from '@/components/shared/StatusProgress'
import HeaderButtonGroup from '@/components/shared/headerbuttons/HeaderButtonGroup'
import BadgeBatch from '@/components/shared/BadgeBatch'

Vue.use(VueNotifications, notificationOptions)

export default {
  name: 'ResultDetail',
  components: {
    BasicHeading,
    PermissionAlertGate,
    ResultCard,
    ContextPills,
    ResultSampleAnalyteTable,
    StatusProgress,
    HeaderButtonGroup,
    BadgeBatch
  },
  mixins: [ToastNotifications],
  data () {
    return {
      loading: true,
      submission_id: this.$route.params.submission_id || null,
      submission: null,
      results: null,
      result: null,
      samples: null,

      submission_badges: null,
      submission_items: null,
      submission_status_logs: null,
      isDownloading: false
    }
  },
  computed: {
    button_config () {
      return {
        buttons: [
          {
            icon: 'fa-eye',
            variant: 'dark',
            text: 'Submission',
            linkto: `/submissions/${this.submission_id}`
          },
          {
            icon: 'fa-chart-area'
          }
        ]
      }
    },
    current_status () {
      return (
        this.submission &&
        this.submission.current_submission_status_log &&
        this.submission.current_submission_status_log.submission_status.status
      )
    }
  },
  created () {
    this.init()
  },
  methods: {
    async fetchSubmission () {
      this.submission = await getModel({
        model_type: 'submission',
        id: this.submission_id,
        message: this.$bvToast
      })
    },

    async fetchResult () {
      this.results = await getCollection({
        collection_type: 'result',
        filters: [
          {
            logic: 'filterEquals',
            field: 'lims_id',
            value: this.submission_id
          }
        ]
      })

      if (this.results && _.isArray(this.results.models) && this.results.models.length) {
        this.result = _.first(this.results.models)
      }
    },

    async fetchSamples () {
      const samples = new SampleCollection()
      const filter = samples.getFilter()

      filter.filterEquals('result_id', this.result.id)

      return samples.fetch().then(() => {
        this.samples = samples
      })
    },

    async init () {
      await this.fetchSubmission()
      await this.fetchResult()

      if (this.result && this.result.id) {
        await this.fetchSamples()
      }

      this.submission_badges = [
        this.submission.event_code,
        this.submission.identifier
      ]

      this.submission_status_logs = this.submission.getSubmissionStatusLogs()

      const project = this.submission.getProject()
      const sample_type = this.submission.getSampleType()
      const test_package = this.submission.getTestPackage()
      const turnaround = this.submission.getTurnaround()

      this.submission_items = [
        {
          to: `/projects/${project.id}`,
          faIcon: project.icon(),
          variant: 'dark',
          label: project.publicNumber()
        },
        {
          to: `/sampletypes/${sample_type.id}`,
          faIcon: sample_type.icon(),
          variant: 'dark',
          label: sample_type.name
        },
        {
          to: `/testpackages/${test_package.id}`,
          faIcon: test_package.icon(),
          variant: 'dark',
          label: test_package.name
        },
        {
          to: `/turnarounds/${turnaround.id}`,
          faIcon: turnaround.icon(),
          variant: 'dark',
          label: turnaround.name
        }
      ]

      this.loading = false
    },

    async downloadFile (type) {
      if (type === 'csv') {
        this.$bvToast.toast('CSV file is not available to download', {
          title: 'Download',
          autoHideDelay: 5000,
          appendToast: true,
          variant: 'warning'
        })
      } else if (type === 'modus') {
        this.isDownloading = true

        try {
          const { response } = await this.result.getModus()

          if (response && response.status === 200 && response.data) {
            this.$bvToast.toast(`Downloading ${type} file...`, {
              title: 'Download',
              autoHideDelay: 5000,
              appendToast: true,
              variant: 'info'
            })

            const { data } = response.data
            const xmlData = data.result

            const blob = new Blob([xmlData], { type: 'text/xml' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `modus_result_${this.result.id}.xml`
            link.click()
          } else {
            throw new Error('Error downloading MODUS file')
          }
        } catch (error) {
          this.$bvToast.toast('Error downloading MODUS file', {
            title: 'Download',
            autoHideDelay: 5000,
            appendToast: true,
            variant: 'danger'
          })
        }

        this.isDownloading = false
      }
    }
  }
}
</script>
